import { useState, useEffect } from 'react';
import { AlertTriangle, Plus, CheckCircle, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ShiftPlanningPanel from '../../components/forecasts/ShiftPlanningPanel';
import { useForecasts } from '../../hooks/useForecasts';
import { useAuthStore } from '../../stores/auth.store';

interface LocationState {
  forecastId?: string;
  successMessage?: string;
  generatedAt?: string;
}

export default function ShiftPlanningPage() {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const { data: forecasts, isLoading } = useForecasts(user?.organizationId);
  const [selectedForecastId, setSelectedForecastId] = useState<string>('');
  const [successNotification, setSuccessNotification] = useState<string | null>(null);

  // Handle navigation state from shift generation
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.forecastId) {
      setSelectedForecastId(state.forecastId);
    }
    if (state?.successMessage) {
      setSuccessNotification(state.successMessage);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => setSuccessNotification(null), 5000);
      // Clear state to prevent showing again on refresh
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Use selected forecast or default to most recent
  const currentForecast = selectedForecastId
    ? forecasts?.find(f => f.id === selectedForecastId) || forecasts?.[0]
    : forecasts?.[0];

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Show empty state if no forecasts exist
  if (!forecasts || forecasts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Forecasts Available</h2>
          <p className="text-gray-600 mb-6">
            You need to create a forecast before you can plan shifts. Forecasts provide the demand data needed to determine staffing requirements.
          </p>
          <Link
            to="/forecasts"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Create a Forecast</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Notification */}
      {successNotification && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 flex items-center space-x-3 max-w-md animate-in slide-in-from-right">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 font-medium flex-1">{successNotification}</p>
          <button
            onClick={() => setSuccessNotification(null)}
            className="text-green-600 hover:text-green-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Forecast Selector */}
      {forecasts.length > 1 && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Forecast
          </label>
          <select
            value={currentForecast?.id || ''}
            onChange={(e) => setSelectedForecastId(e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {forecasts.map((forecast) => (
              <option key={forecast.id} value={forecast.id}>
                {forecast.name} - {forecast.modelType}
              </option>
            ))}
          </select>
        </div>
      )}

      {currentForecast && (
        <ShiftPlanningPanel forecastId={currentForecast.id} />
      )}
    </div>
  );
}
